import React, { useState, useEffect } from 'react';
import { HiOutlineCalendarDays, HiOutlineCube, HiOutlineCurrencyDollar } from 'react-icons/hi2';
import api from '../utils/api';
import './AdminServiceAnalytics.css';

const AdminServiceAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    totalServiceBookings: 0,
    totalOrders: 0,
    totalRevenue: 0,
    servicesByStatus: {},
    ordersByStatus: {},
    topServices: [],
    topProducts: [],
    monthlyData: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('services');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Fetch users for the users tab
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
    // eslint-disable-next-line
  }, [activeTab]);
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.items || []);
    } catch (err) {
      setUsersError(err.response?.data?.message || 'Хэрэглэгч авах үед алдаа гарлаа');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      const [bookingsRes, ordersRes] = await Promise.all([
        api.get('/admin/metrics'),
        api.get('/orders')
      ]);

      const metrics = bookingsRes.data;
      const orders = ordersRes.data.items || [];

      // Calculate stats
      const servicesByStatus = {};
      const ordersByStatus = {};
      let totalRevenue = 0;
      const topServices = {};
      const topProducts = {};

      if (metrics.bookingStats) {
        metrics.bookingStats.forEach(stat => {
          servicesByStatus[stat.status] = stat.count;
        });
      }

      orders.forEach(order => {
        ordersByStatus[order.status] = (ordersByStatus[order.status] || 0) + 1;
        totalRevenue += order.totalAmount || 0;
      });

      setAnalytics({
        totalServiceBookings: metrics.totalBookings || 0,
        totalOrders: orders.length,
        totalRevenue,
        servicesByStatus,
        ordersByStatus,
        topServices: [],
        topProducts: [],
        monthlyData: metrics.monthlyTrend || []
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Анализ авах үед алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    pending: '#FFC107',
    confirmed: '#2196F3',
    completed: '#4CAF50',
    cancelled: '#F44336',
    rejected: '#F44336'
  };

  const renderPieChart = (data, colors = statusColors) => {
    const total = Object.values(data).reduce((a, b) => a + b, 0);
    if (total === 0) return <p>Өгөгдөл байхгүй</p>;

    return (
      <div className="pie-chart-container">
        <div className="pie-chart">
          {Object.entries(data).map(([status, count], index) => {
            const percentage = (count / total) * 100;
            const angle = (count / total) * 360;
            return (
              <div
                key={status}
                className="pie-segment"
                style={{
                  '--segment-angle': `${angle}deg`,
                  '--segment-color': colors[status] || '#667eea'
                }}
                title={`${status}: ${count} (${percentage.toFixed(1)}%)`}
              />
            );
          })}
        </div>
        <div className="pie-legend">
          {Object.entries(data).map(([status, count]) => {
            const percentage = (count / total) * 100;
            return (
              <div key={status} className="legend-item">
                <span
                  className="legend-color"
                  style={{ background: colors[status] || '#667eea' }}
                />
                <span className="legend-label">
                  {status}: {count} ({percentage.toFixed(1)}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-service-analytics">
      <div className="analytics-header">
        <h2>Үйлчилгээ болон захиалгын судалгаа</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <p>Анализ хийж байна...</p>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-icon">
                <HiOutlineCalendarDays className="icon-svg" />
              </div>
              <div className="kpi-content">
                <div className="kpi-label">Үйлчилгээний захиалга</div>
                <div className="kpi-value">{analytics.totalServiceBookings}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">
                <HiOutlineCube className="icon-svg" />
              </div>
              <div className="kpi-content">
                <div className="kpi-label">Нийт захиалга</div>
                <div className="kpi-value">{analytics.totalOrders}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">
                <HiOutlineCurrencyDollar className="icon-svg" />
              </div>
              <div className="kpi-content">
                <div className="kpi-label">Нийт орлого</div>
                <div className="kpi-value">{analytics.totalRevenue.toLocaleString()} ₮</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="analytics-tabs">
            <button
              className={`tab-button ${activeTab === 'services' ? 'active' : ''}`}
              onClick={() => setActiveTab('services')}
            >
              Үйлчилгээ төлөв
            </button>
            <button
              className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Захиалгын төлөв
            </button>
            <button
              className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              Хэрэглэгчид
            </button>
          </div>


          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'users' && (
              <div className="users-section">
                <h3>Бүртгэлтэй хэрэглэгчид</h3>
                {usersError && <div className="error-message">{usersError}</div>}
                {usersLoading ? (
                  <p>Уншиж байна...</p>
                ) : users.length === 0 ? (
                  <p>Хэрэглэгч байхгүй</p>
                ) : (
                  <div className="users-table-container">
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th>Нэр</th>
                          <th>И-мэйл</th>
                          <th>Утас</th>
                          <th>Үүсгэгдсэн</th>
                          <th>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user._id}>
                            <td>{user.username}</td>
                            <td>{user.email}</td>
                            <td>{user.phone || '-'}</td>
                            <td>{new Date(user.createdAt).toLocaleDateString('mn-MN')}</td>
                            <td>{user.role}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'services' && (
              <div className="chart-section">
                <h3>Үйлчилгээний захиалгын төлөв</h3>
                {Object.keys(analytics.servicesByStatus).length > 0 ? (
                  renderPieChart(analytics.servicesByStatus)
                ) : (
                  <p>Өгөгдөл байхгүй</p>
                )}

                <div className="stats-table">
                  <h4>Үйлчилгээний статусын нарийн</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Төлөв</th>
                        <th>Тоо</th>
                        <th>Хувь</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(analytics.servicesByStatus).map(([status, count]) => {
                        const total = Object.values(analytics.servicesByStatus).reduce((a, b) => a + b, 0);
                        const percentage = ((count / total) * 100).toFixed(1);
                        return (
                          <tr key={status}>
                            <td>
                              <span className="status-badge" style={{ background: statusColors[status] || '#667eea' }}>
                                {status}
                              </span>
                            </td>
                            <td>{count}</td>
                            <td>{percentage}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'orders' && (
              <div className="chart-section">
                <h3>Захиалгын төлөв</h3>
                {Object.keys(analytics.ordersByStatus).length > 0 ? (
                  renderPieChart(analytics.ordersByStatus)
                ) : (
                  <p>Өгөгдөл байхгүй</p>
                )}

                <div className="stats-table">
                  <h4>Захиалгын статусын нарийн</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Төлөв</th>
                        <th>Тоо</th>
                        <th>Хувь</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(analytics.ordersByStatus).map(([status, count]) => {
                        const total = Object.values(analytics.ordersByStatus).reduce((a, b) => a + b, 0);
                        const percentage = ((count / total) * 100).toFixed(1);
                        return (
                          <tr key={status}>
                            <td>
                              <span className="status-badge" style={{ background: statusColors[status] || '#667eea' }}>
                                {status}
                              </span>
                            </td>
                            <td>{count}</td>
                            <td>{percentage}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminServiceAnalytics;
